import Fuse from 'fuse.js';

import { panel } from '@atlaskit/adf-schema/schema-validator';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import type { Fragment, Node, NodeType, ResolvedPos } from '@atlaskit/editor-prosemirror/model';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import { findWrapping } from '@atlaskit/editor-prosemirror/transform';
import { fg } from '@atlaskit/platform-feature-flags';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';

import type {
	WrapNodeCommand,
	StreamingCommandFactory,
} from '../aiStreamingOrchestratorPluginType';

import { findNodeByLocalId } from './utils/find-node-by-local-id';

const panelTypes = new Fuse(panel.props.attrs.props.panelType.values);

function wrapChild(
	fragment: Fragment,
	type: NodeType,
	attrs: Record<string, unknown>,
): Node | null {
	if (!fragment.firstChild) {
		return null;
	}

	const wrappingNodeTypes = type.contentMatch.findWrapping(fragment.firstChild.type);
	let currentChildNodes: readonly Node[] | null = null;
	if (wrappingNodeTypes) {
		currentChildNodes = fragment.content;
		for (let i = wrappingNodeTypes.length - 1; i >= 0; i--) {
			const wrappingNodeType = wrappingNodeTypes[i];
			const nextChildNode = wrappingNodeType.createAndFill(null, currentChildNodes);
			if (nextChildNode) {
				currentChildNodes = [nextChildNode];
			} else {
				currentChildNodes = null;
				break;
			}
		}
	}
	if (currentChildNodes) {
		return type.createAndFill(attrs, currentChildNodes);
	}

	return null;
}

function findPosForNode(node: Node, $to: ResolvedPos): number | null {
	for (let depth = $to.depth; depth >= 0; depth--) {
		const ancestor = $to.node(depth);
		if (ancestor.type.contentMatch.matchType(node.type)) {
			return $to.after(depth + 1);
		}
	}
	return null;
}

function getAncestorTypes($to: ResolvedPos): string[] {
	const ancestors: string[] = [];
	for (let depth = $to.depth; depth >= 0; depth--) {
		const ancestor = $to.node(depth);
		ancestors.push(ancestor.type.name);
	}
	return ancestors;
}

function getChildTypes(fragment: Fragment): string[] {
	return fragment.content.map((node) => node.type.name);
}

export const createWrapNodeCommand: StreamingCommandFactory<WrapNodeCommand> =
	(api) => (command) => {
		let success = false;
		let error: unknown;
		let repaired = false;
		let ancestors: string[] = [];
		let fragments: string[] = [];
		api.core.actions.execute(({ tr }) => {
			try {
				findNodeByLocalId(api, tr, command.localId, ({ node, pos }) => {
					const $from = tr.doc.resolve(pos);
					const $to = tr.doc.resolve(pos + node.nodeSize);
					const range = $from.blockRange($to);
					if (range) {
						const type = tr.doc.type.schema.nodes[command.wrapperType];
						const attrs = command.wrapperAttrs;
						// TODO: EDITOR-1371 - Validate attributes for all nodes
						if (command.wrapperType === 'panel' && attrs.panelType !== undefined) {
							const panelType = command.wrapperAttrs.panelType as string;
							const panelTypeResult = panelTypes.search(panelType);
							if (panelTypeResult.length) {
								attrs.panelType = panelTypeResult[0].item;
							} else {
								attrs.panelType = 'info';
							}
						}
						try {
							// NOTE: `wrapping` will be empty ([]) when it fits directly
							const wrapping = findWrapping(range, type, attrs);
							if (wrapping !== null) {
								tr.wrap(range, wrapping).scrollIntoView();
								success = true;
							} else {
								const slice = tr.doc.slice(range.$from.pos, range.$to.pos);
								const fragment = slice.content;
								let node = type.createAndFill(attrs, fragment);
								if (!node) {
									node = wrapChild(fragment, type, attrs);
								}
								if (node) {
									const insertPos = findPosForNode(node, $to);
									if (insertPos !== null) {
										tr.insert(insertPos, node).scrollIntoView();
										tr.replaceRange(range.$from.pos, range.$to.pos, Slice.empty);
										success = true;
										repaired = true;
									}
								}
								ancestors = getAncestorTypes($to);
								fragments = getChildTypes(fragment);
							}
						} catch (e) {
							error = e;
							// TODO: EDITOR-1370 - Log the error
							// eslint-disable-next-line no-console
							console.error(e);
						}
					}
				});
				if (fg('platform_editor_ai_instrumentation_updates')) {
					api.aiExperience.actions.tagAsAIStreamingTransformation(tr, { isFinal: true });
				}
			} catch (e) {
				error = e;
				// TODO: EDITOR-1370 - Log the error
				// eslint-disable-next-line no-console
				console.error(e);
			}
			return tr;
		});

		if (!success || repaired) {
			// Log the error
			const errorMessage = ErrorUtils.extractErrorMessage(error);
			let errorStack = '';
			if (error instanceof Error) {
				errorStack = error.stack || '';
			}
			api?.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.INVALID_COMMAND_FOUND,
				actionSubject: ACTION_SUBJECT.AI_STREAMING,
				actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					// Disable for now #hot-122604
					// command,
					success,
					repaired,
					ancestors,
					fragments,
					errorMessage,
					errorStack,
				},
			});
		}

		return success;
	};
